import parseByDelimiter from '@utils/parseByDelimiter'
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Platform } from 'react-native'
import {
  AlertDialog,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
  Button,
  XStack,
  YStack,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogPortal,
  TextArea,
} from 'tamagui'

type ConfirmOptions = {
  cancelText?: string
  confirmText?: string
  customCallbackComponent?: React.ReactElement
  textField?: boolean
  initialValue?: string
}

type AlertDialogContextType = {
  showAlert: (title: string, message?: string) => Promise<void>
  showConfirm: (
    title: string,
    message?: string,
    options?: ConfirmOptions
  ) => Promise<boolean | string | undefined>
}

export const AlertDialogContext = createContext<AlertDialogContextType | undefined>(undefined)

export const AlertDialogProvider = ({ children }: { children: ReactNode, textField?: boolean, initialValue?: string }) => {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [mode, setMode] = useState<'alert' | 'confirm'>('alert')
  const [confirmOptions, setConfirmOptions] = useState<ConfirmOptions>({})
  const resolveRef = useRef<((value: boolean | string | void) => void) | undefined>(undefined)
  const [textFieldValue, setTextFieldValue] = useState<string | undefined>(undefined);
  const [textField, setTextField] = useState<boolean>(false);
  const customComponentRef = useRef<any>(undefined);
  const originalBodyPaddingRightRef = useRef<string | null>(null)
  const originalRootScrollbarGutterRef = useRef<string | null>(null)

  const showAlert = useCallback((title: string, message = '',) => {
    return new Promise<void>((resolve) => {
      setTitle(title)
      setMessage(message)
      setMode('alert');
      resolveRef.current = resolve as unknown as (value: boolean | string | void) => void
      setOpen(true)
    })
  }, [])

  const showConfirm = useCallback(
    (title: string, message = '', options: ConfirmOptions = {}) => {
      return new Promise<boolean | string | undefined>((resolve) => {
        setTitle(title)
        setTextField(options.textField)
        setTextFieldValue(parseByDelimiter(options.initialValue))
        setMessage(message)
        setConfirmOptions(options)
        setMode('confirm')
        resolveRef.current = resolve as unknown as (value: boolean | string | void) => void
        setOpen(true)
      })
    },
    []
  )

  React.useEffect(() => {
  }, [customComponentRef.current]);

  // Web-only: prevent layout "shift + snap back" when the dialog opens/closes.
  // The common cause is body scroll-lock removing the vertical scrollbar, changing viewport width.
  useEffect(() => {
    if (Platform.OS !== 'web') return
    if (typeof window === 'undefined' || typeof document === 'undefined') return

    const root = document.documentElement
    const body = document.body

    // Persist original values once.
    if (originalBodyPaddingRightRef.current === null) {
      originalBodyPaddingRightRef.current = body.style.paddingRight ?? ''
    }
    if (originalRootScrollbarGutterRef.current === null) {
      originalRootScrollbarGutterRef.current = root.style.scrollbarGutter ?? ''
    }

    // Keep gutter stable where supported (no-op in unsupported browsers).
    root.style.scrollbarGutter = 'stable'

    if (open) {
      // Compute scrollbar width; if > 0, add matching right padding to keep layout width stable.
      const scrollBarWidth = window.innerWidth - root.clientWidth
      if (scrollBarWidth > 0) {
        body.style.paddingRight = `${scrollBarWidth}px`
      }
    } else {
      // Restore original padding when closed.
      body.style.paddingRight = originalBodyPaddingRightRef.current ?? ''
    }

    return () => {
      // On unmount, restore original values.
      body.style.paddingRight = originalBodyPaddingRightRef.current ?? ''
      root.style.scrollbarGutter = originalRootScrollbarGutterRef.current ?? ''
    }
  }, [open])

  const handleClose = (result: boolean | string | void) => {
    setOpen(false)
    resolveRef.current?.(result)
  }

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({ showAlert, showConfirm }),
    [showAlert, showConfirm]
  )

  return (
    <AlertDialogContext.Provider value={contextValue}>
      {children}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogPortal style={{paddingHorizontal: "5%"}}>
          <AlertDialogOverlay
            key="overlay"
            animation="quick"
            opacity={0.7}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
            backgroundColor="$background" 
          />
          <AlertDialogContent
            elevate
            bordered
            borderColor="$border"
            backgroundColor="$card"
            borderRadius={Platform.OS === 'web' ? "$2xl" : 16}
            padding="$6"
            minWidth={"90%"}
            maxWidth={500}
            key="content"
            animation={[
              'quick',
              {
                opacity: { overshootClamping: true },
              },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            x={0}
            scale={1}
            opacity={1}
            y={0}
            shadowColor="$shadowColor"
            shadowRadius={20}
            shadowOffset={{ width: 0, height: 10 }}
            shadowOpacity={0.1}
          >
            <YStack gap="$4">
              <AlertDialogTitle fontWeight="bold" fontSize={Platform.OS === 'web' ? "$2xl" : 20} color="$foreground">{title}</AlertDialogTitle>
              <AlertDialogDescription size="$md" color="$mutedForeground">{message}</AlertDialogDescription>
              {
                textField && (
                    <TextArea
                      value={textFieldValue}
                      returnKeyLabel='Done'
                      style={{ marginHorizontal: -20 }}
                      enterKeyHint='done'
                      onChangeText={(text) => setTextFieldValue(text)}
                    borderColor="$border"
                    borderRadius="$sm"
                    backgroundColor="$background"
                    color="$foreground"
                    />
                )
              }
                {
                confirmOptions.customCallbackComponent && mode === 'confirm' && (
                  React.cloneElement(
                    confirmOptions.customCallbackComponent as any,
                  { ref: customComponentRef }
                  )
                )
                }
              <XStack gap="$3" justifyContent="flex-end" marginTop="$2">
                {mode === 'confirm' && (
                  <AlertDialogCancel asChild>
                    <Button
                      onPress={() => handleClose(false)}
                      backgroundColor="transparent"
                      borderColor="$border"
                      borderWidth={1}
                      borderRadius="$xl"
                      textProps={{ color: '$mutedForeground' }}
                      hoverStyle={{ borderColor: '$foreground' }}
                      pressStyle={{ opacity: 0.8 }}
                    >
                      {confirmOptions.cancelText ?? 'Cancel'}
                    </Button>
                  </AlertDialogCancel>
                )}
                <AlertDialogAction asChild>
                  <Button
                    theme={mode === 'confirm' ? 'accent' : undefined}
                    onPress={() => handleClose(
                      textField ? textFieldValue : (mode === 'confirm' ? (confirmOptions.customCallbackComponent ? customComponentRef.current?.getValue() : true) : undefined))}
                    backgroundColor="rgb(90,192,234)"
                    borderRadius="$xl"
                    textProps={{ color: '$primaryForeground', fontWeight: '600' }}
                    hoverStyle={{ backgroundColor: 'rgb(90,192,234)' }}
                    pressStyle={{ opacity: 0.9 }}
                  >
                    {mode === 'confirm'
                      ? confirmOptions.confirmText ?? 'Confirm'
                      : 'OK'}
                  </Button>
                </AlertDialogAction>
              </XStack>
            </YStack>
          </AlertDialogContent>
        </AlertDialogPortal>
      </AlertDialog>
    </AlertDialogContext.Provider>
  )
}
